import React, { useMemo, useEffect } from 'react';

/**
 * Componente para renderizar um slot individual
 */
const TimeSlot = React.memo(({ 
  slot, 
  selectedTime, 
  onTimeSelect, 
  primaryColor,
  isAdmin = false
}) => {
  // Extrair dados do slot
  const timeValue = typeof slot === 'object' ? slot.time : slot;
  const isSelected = selectedTime === timeValue;
  
  // Determinar estados visuais
  const isAvailable = typeof slot === 'object' ? slot.available : true;
  const isBlocked = typeof slot === 'object' ? slot.isBlocked : false;
  const hasConflicts = typeof slot === 'object' ? slot.hasConflicts : false;
  
  // Log para entender os estados dos slots
  useEffect(() => {
    if (typeof slot === 'object') {
      // Analisar estrutura completa do slot
      console.log(`TimeSlot - Estrutura COMPLETA do slot ${timeValue}:`, JSON.stringify(slot, null, 2));
      
      // Log detalhado dos conflitos
      if (slot.conflicts && slot.conflicts.length > 0) {
        console.log(`TimeSlot ${timeValue} - Análise de conflitos (${slot.conflicts.length}):`, 
          slot.conflicts.map(c => ({
            tipo: c.type,
            profissionalId: c.professionalId,
            allowsOverlap: c.allowsOverlap,
            details: c
          }))
        );
      }
      
      // Verificar se o slot tem professionals
      if (slot.professionals) {
        console.log(`TimeSlot ${timeValue} - Professionals encontrados:`, slot.professionals);
      } else {
        console.log(`TimeSlot ${timeValue} - Não tem array de professionals!`);
      }
    }
  }, [slot, timeValue]);
  
  // Determinar classes com base nos estados
  const baseClasses = "py-2 px-2 rounded text-center text-sm transition-all";
  const stateClasses = useMemo(() => {
    if (isSelected) {
      // Admin mode - azul claro com borda azul
      if (isAdmin) {
        return 'border-2 border-blue-500 bg-blue-50 text-blue-500 font-medium';
      }
      // Cliente mode - cor primária do salão
      return 'text-white font-medium';
    }
    
    if (!isAvailable) {
      if (isBlocked) {
        return "border border-red-200 bg-red-50 text-red-800 opacity-60 cursor-not-allowed";
      }
      
      if (hasConflicts) {
        return "border border-orange-200 bg-orange-50 text-orange-800 opacity-60 cursor-not-allowed";
      }
      
      return "border border-gray-200 bg-gray-50 text-gray-400 opacity-60 cursor-not-allowed";
    }
    
    return "border border-gray-200 hover:bg-gray-100 cursor-pointer";
  }, [isSelected, isAvailable, isBlocked, hasConflicts, isAdmin]);
  
  // Log quando o slot é clicado
  const handleClick = () => {
    console.log(`TimeSlot - Clique no slot ${timeValue}:`, {
      isAvailable,
      isSelected,
      isBlocked,
      hasConflicts,
      blockingReason: typeof slot === 'object' ? slot.blockingReason : 'N/A'
    });
    
    if (isAvailable) {
      onTimeSelect(timeValue);
    } else {
      console.log(`TimeSlot - Clique ignorado: slot ${timeValue} não está disponível`);
    }
  };
  
  return (
    <div
      className={`${baseClasses} ${stateClasses}`}
      style={isSelected && !isAdmin ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
      onClick={handleClick}
      title={
        !isAvailable
          ? isBlocked
            ? "Horário bloqueado"
            : hasConflicts
              ? "Horário ocupado"
              : "Horário indisponível"
          : ""
      }
    >
      {timeValue}
      
      {/* Indicadores de status para slots indisponíveis */}
      {!isAvailable && (
        <div className="text-xs mt-1">
          {isBlocked && <span className="block">Bloqueado</span>}
          {!isBlocked && hasConflicts && <span className="block">Ocupado</span>}
        </div>
      )}
    </div>
  );
});

/**
 * Componente que renderiza uma grade de horários disponíveis
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.availableTimeSlots - Lista de horários disponíveis
 * @param {string} props.selectedTime - Horário selecionado
 * @param {Function} props.onTimeSelect - Função chamada ao selecionar um horário
 * @param {string} props.primaryColor - Cor primária para destaque
 * @param {boolean} props.showAllSlots - Mostrar todos os slots, não apenas disponíveis
 * @param {boolean} props.isAdmin - Indica se está no modo admin
 */
const TimeSlotGrid = ({
  availableTimeSlots = [],
  selectedTime,
  onTimeSelect,
  primaryColor = '#4F46E5',
  showAllSlots = false,
  isAdmin = false
}) => {
  // Log para entender os dados recebidos
  useEffect(() => {
    console.log('TimeSlotGrid - Props recebidas:', {
      totalSlots: availableTimeSlots?.length || 0,
      selectedTime,
      showAllSlots
    });
    
    if (availableTimeSlots && availableTimeSlots.length > 0) {
      // Log mais detalhado da estrutura dos slots
      console.log('TimeSlotGrid - Estrutura de todos os slots:', 
        availableTimeSlots.map(slot => ({
          hora: slot.time,
          disponível: slot.available,
          conflitos: slot.hasConflicts,
          bloqueado: slot.isBlocked,
          motivo: slot.blockingReason,
          temProfissionais: !!slot.professionals,
          qtdProfissionais: slot.professionals ? slot.professionals.length : 0
        }))
      );
      
      // Procurar por slot das 09:00 (mencionado no problema original)
      const nineAmSlot = availableTimeSlots.find(s => s.time === '09:00');
      if (nineAmSlot) {
        console.log('TimeSlotGrid - Detalhes do slot 09:00:', nineAmSlot);
        
        // Verificar conflitos específicos deste slot
        if (nineAmSlot.conflicts && nineAmSlot.conflicts.length > 0) {
          console.log('TimeSlotGrid - Conflitos do slot 09:00:', nineAmSlot.conflicts);
        }
      }
    }
  }, [availableTimeSlots, selectedTime, showAllSlots]);
  
  // Verificar formato dos dados (string ou objeto)
  const isObjectFormat = availableTimeSlots.length > 0 && typeof availableTimeSlots[0] === 'object';
  
  // Filtrar apenas horários disponíveis se estivermos no formato de objeto e não estamos mostrando todos os slots
  const timeSlotsToDisplay = useMemo(() => {
    console.log('TimeSlotGrid - Iniciando filtragem de slots', {
      total: availableTimeSlots?.length || 0,
      showAllSlots
    });
    
    if (!availableTimeSlots || availableTimeSlots.length === 0) return [];
    if (!isObjectFormat) return availableTimeSlots;
    
    // Simplificação da lógica para mostrar apenas horários com pelo menos um profissional disponível
    const result = showAllSlots 
      ? availableTimeSlots 
      : availableTimeSlots.filter(slot => {
          // Se o slot está diretamente disponível
          if (slot.available) return true;
          
          // Se existem profissionais e pelo menos um deles está disponível
          if (slot.professionals && slot.professionals.some(p => p.available)) return true;
          
          // Não mostrar horários completamente bloqueados
          return false;
        });
    
    // Log detalhado da filtragem
    console.log('TimeSlotGrid - Resultado da filtragem:', {
      slotsAntes: availableTimeSlots.length,
      slotsDepois: result.length,
      slotsRemovidos: availableTimeSlots.length - result.length,
      horariosExibidos: result.map(s => s.time),
      horariosRemovidos: availableTimeSlots
        .filter(s => !result.some(r => r.time === s.time))
        .map(s => s.time)
    });
    
    return result;
  }, [availableTimeSlots, isObjectFormat, showAllSlots]);

  // Verificação de slots vazios após o cálculo dos timeSlotsToDisplay
  if (!timeSlotsToDisplay || timeSlotsToDisplay.length === 0) {
    console.log('TimeSlotGrid - Nenhum slot disponível para exibição');
    return (
      <div className="py-4 text-center">
        <p className="text-gray-500">Nenhum horário disponível para esta data</p>
        <p className="text-sm text-gray-400 mt-1">Tente selecionar outra data para ver os horários</p>
      </div>
    );
  }

  // Modificar o componente TimeSlot para indicar profissionais disponíveis
  const handleSlotClick = (slot) => {
    console.log(`TimeSlotGrid - Selecionando slot:`, {
      horario: typeof slot === 'object' ? slot.time : slot,
      profissionaisDisponiveis: typeof slot === 'object' && slot.professionals 
        ? slot.professionals.filter(p => p.available).map(p => p.nome) 
        : 'todos'
    });
    
    const timeValue = typeof slot === 'object' ? slot.time : slot;
    onTimeSelect(timeValue);
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
      {timeSlotsToDisplay.map((slot) => (
        <TimeSlot
          key={typeof slot === 'object' ? slot.time : slot}
          slot={slot}
          selectedTime={selectedTime}
          onTimeSelect={() => handleSlotClick(slot)}
          primaryColor={primaryColor}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
};

export default React.memo(TimeSlotGrid); 